import { useCallback, useEffect, useMemo, useState } from 'react'

import { Autocomplete, cn, Description, EmptyState, Label, ListBox, SearchField, Spinner } from '@heroui/react'
import { useTranslation } from 'react-i18next'

import { vehiclesApi } from '@/api/vehicles.api'
import { useVehicles } from '@/hooks/useVehicles'
import type { Vehicle } from '@/types/vehicle'

import { Item } from './Item'

interface VehicleAutocompleteProps {
  label?: string;
  description?: string;
  placeholder?: string;
  onSelectionChange?: (vehicle: Vehicle | null) => void;
  className?: string;
  selectedId?: string;
  initialValue?: Vehicle | null;
  isInvalid?: boolean;
  isRequired?: boolean;
  errorMessage?: string;
}

export default ({
  label,
  description,
  placeholder,
  onSelectionChange,
  className,
  selectedId,
  initialValue,
  isInvalid,
  isRequired,
}: VehicleAutocompleteProps) => {
  const { t } = useTranslation()
  const [search, setSearch] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [isFocused, setIsFocused] = useState(false)
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null)

  const hasSelectedValue = Boolean(selectedId && selectedId !== 'none')
  const enabled = isOpen ? true : isFocused ? true : hasSelectedValue
  const { data, isLoading } = useVehicles(search, enabled)

  useEffect(() => {
    if (!selectedId || selectedId === 'none') {
      setSelectedVehicle(null)
      return
    }

    if (selectedVehicle?.ID === selectedId) {
      return
    }

    const found = data.find(v => v.ID === selectedId) || (initialValue?.ID === selectedId ? initialValue : null)
    if (found) {
      setSelectedVehicle(found)
      return
    }

    let isMounted = true
    vehiclesApi.getVehicle(selectedId)
      .then(vehicle => {
        if (isMounted && vehicle) {
          setSelectedVehicle(vehicle)
        }
      })
      .catch(err => {
        console.error('Error fetching vehicle details:', err)
      })

    return () => {
      isMounted = false
    }
  }, [selectedId, data, initialValue, selectedVehicle])

  const items = useMemo(() => {
    const list = [...data]
    if (initialValue && !list.some(v => v.ID === initialValue.ID)) {
      list.unshift(initialValue)
    }
    if (selectedVehicle && selectedVehicle.ID === selectedId && !list.some(v => v.ID === selectedVehicle.ID)) {
      list.unshift(selectedVehicle)
    }
    return list
  }, [data, initialValue, selectedVehicle, selectedId])

  const finalPlaceholder = placeholder || t('vehicle_autocomplete.placeholder')

  const handleOpenChange = useCallback((open: boolean) => {
    setIsOpen(open)
  }, [])

  const handleFocus = useCallback(() => {
    setIsFocused(true)
  }, [])

  const handleBlur = useCallback(() => {
    setIsFocused(false)
  }, [])

  const handleSelectionChange = useCallback((key: React.Key | null) => {
    const vehicle = items.find(v => v.ID === key) ?? null
    onSelectionChange?.(vehicle)
  }, [items, onSelectionChange])

  return (
    <Autocomplete
      items={items as any}
      allowsEmptyCollection
      variant={'secondary'}
      className={className}
      selectedKey={selectedId}
      isInvalid={isInvalid}
      isRequired={isRequired}
      onSelectionChange={handleSelectionChange}
      onOpenChange={handleOpenChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
    >
      {label && <Label>{label}</Label>}
      <Autocomplete.Trigger className="w-full min-w-0 overflow-hidden">
        <Autocomplete.Value className="w-full min-w-0 overflow-hidden">
          {({ isPlaceholder, state }) => {
            if (isPlaceholder) return finalPlaceholder

            const selectedKey = state.selectedKey
            const item = items.find(v => v.ID === selectedKey)

            if (!item) return null

            return (
              <div className="flex items-center gap-2 w-full min-w-0 overflow-hidden whitespace-nowrap">
                <span className="font-medium text-sm truncate min-w-0">{item.Number}</span>
                <span className="text-zinc-400 text-xs flex-shrink-0">—</span>
                <span className="text-xs text-zinc-500 truncate min-w-0">{item.Model}</span>
              </div>
            )
          }}
        </Autocomplete.Value>
        {selectedId && selectedId !== 'none' && (
          <div onClick={e => e.stopPropagation()}>
            <Autocomplete.ClearButton />
          </div>
        )}
        <Autocomplete.Indicator />
      </Autocomplete.Trigger>
      {description && <Description>{description}</Description>}
      <Autocomplete.Popover>
        <Autocomplete.Filter>
          <SearchField
            onChange={val => {
              setSearch(val)
            }}
          >
            <SearchField.Group>
              <SearchField.SearchIcon />
              <SearchField.Input placeholder={t('vehicle_autocomplete.search_placeholder')} autoFocus />
              <Spinner
                size="sm"
                className={cn("absolute top-1/2 right-2 -translate-y-1/2", {
                  "pointer-events-none opacity-0": !isLoading,
                })}
              />
            </SearchField.Group>
          </SearchField>
          <ListBox
            items={items as any}
            renderEmptyState={() => <EmptyState>{t('vehicle_autocomplete.no_results')}</EmptyState>}
          >
            {(item: Vehicle) => (
              <ListBox.Item key={item.ID} id={item.ID} textValue={item.Number} >
                <Item item={item} />
                <ListBox.ItemIndicator />
              </ListBox.Item>
            )}
          </ListBox>
        </Autocomplete.Filter>
      </Autocomplete.Popover>
    </Autocomplete>
  )
}
